import type { AlertLevel, HealthStatus } from "./api/types";

// ── Band thresholds (single source of truth; re-exported by api/stability.ts) ──
export const PSI_WARN = 0.1;
export const PSI_CRIT = 0.25;
export const CSI_WARN = 0.1;
export const CSI_CRIT = 0.25;

export type BandStatus = AlertLevel | "SKIPPED";

/** Classify a raw PSI/CSI number into a band. null/undefined/NaN → SKIPPED. */
export function classifyBand(
  value: number | null | undefined,
  warn: number = PSI_WARN,
  crit: number = PSI_CRIT,
): BandStatus {
  if (value === null || value === undefined || Number.isNaN(value)) return "SKIPPED";
  if (value >= crit) return "CRITICAL";
  if (value >= warn) return "WARN";
  return "STABLE";
}

/** Normalize backend status vocabulary (OK/WARNING/CRITICAL/null) to the UI's. */
export function normalizeStatus(raw: string | null | undefined): BandStatus {
  switch ((raw ?? "").toUpperCase()) {
    case "OK":
    case "STABLE":
      return "STABLE";
    case "WARNING":
    case "WARN":
      return "WARN";
    case "CRITICAL":
      return "CRITICAL";
    default:
      return "SKIPPED";
  }
}

export type DriftVerdict = "DRIFTING" | "STABLE" | "MIXED";
export type DecayVerdict = "TRACKING" | "SEASONING" | "DECAYING";

/** Any CRITICAL → DRIFTING; else any WARN → MIXED; else STABLE. */
export function driftVerdict(statuses: BandStatus[]): DriftVerdict {
  if (statuses.includes("CRITICAL")) return "DRIFTING";
  if (statuses.includes("WARN")) return "MIXED";
  return "STABLE";
}

export function countOpen(statuses: BandStatus[]): {
  openCritical: number;
  openWarn: number;
} {
  return {
    openCritical: statuses.filter((s) => s === "CRITICAL").length,
    openWarn: statuses.filter((s) => s === "WARN").length,
  };
}

/**
 * Overall health light. A demonstrably-wrong model (decay DECAYING) is RED; open
 * drift/warn signals are AMBER; otherwise GREEN. Mirrors the current example — a
 * critical drift *input* plus warns, with decay still SEASONING, reads AMBER.
 */
export function overallHealth(
  drift: DriftVerdict,
  decay: DecayVerdict,
  openCritical: number,
  openWarn: number,
): HealthStatus {
  if (decay === "DECAYING") return "RED";
  if (drift !== "STABLE" || openCritical > 0 || openWarn > 0) return "AMBER";
  return "GREEN";
}
